import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useAuth } from 'context'
import {
    CompanyService,
    ShareEmailSubjectFormatService,
    buildFormattedName,
    buildPdfFilenamePreview,
    DEFAULT_INSPECTION_NAME_CONFIG,
    DEFAULT_PDF_FILENAME_CONFIG,
    getPdfEffectiveSourceLabel,
    parseNameFormatConfig,
    SAMPLE_TEMPLATE_NAME,
    toApiPayload,
    withCompanyTimezone,
} from 'utility'
import { NameFormatLauncher } from './NameFormatLauncher'
import { NameFormatModal } from './NameFormatModal'
import { ShareEmailSubjectFormatModal } from './ShareEmailSubjectFormatModal'

const SAMPLE_AUDIT_ID = 27062

// companyTimeZone = the company TimeZone the server uses for inspection names.
export const CompanyNameFormats = forwardRef(({ enabled = true, draftOnly = true, companyTimeZone }, ref) => {
    const { user } = useAuth()
    const companyId = user?.CompanyId || user?.Companies?.[0]?.CompanyId

    const sample = useMemo(
        () => ({
            templateName: SAMPLE_TEMPLATE_NAME,
            companyName:
                user?.CompanyName ||
                user?.Companies?.[0]?.CompanyName ||
                'Your Company',
            fullName: user?.FullName || '',
            userId: user?.unique_id || user?.UserId || '',
            auditId: SAMPLE_AUDIT_ID,
        }),
        [user],
    )

    const [loading, setLoading] = useState(true)
    const [inspectionConfig, setInspectionConfig] = useState(
        DEFAULT_INSPECTION_NAME_CONFIG,
    )
    const [pdfConfig, setPdfConfig] = useState(DEFAULT_PDF_FILENAME_CONFIG)
    const [pdfEffectiveSource, setPdfEffectiveSource] = useState(null)
    const [hasPdfConfig, setHasPdfConfig] = useState(false)
    const [pdfCustomized, setPdfCustomized] = useState(false)

    const [inspectionModalOpen, setInspectionModalOpen] = useState(false)
    const [pdfModalOpen, setPdfModalOpen] = useState(false)
    const [emailSubjectModalOpen, setEmailSubjectModalOpen] = useState(false)
    const [emailSubjectPreview, setEmailSubjectPreview] = useState('—')
    const [emailSubjectLoading, setEmailSubjectLoading] = useState(false)
    const [emailSubjectLegacy, setEmailSubjectLegacy] = useState(false)

    const inspectionConfigRef = useRef(inspectionConfig)
    const pdfConfigRef = useRef(pdfConfig)
    const hasPdfConfigRef = useRef(hasPdfConfig)
    const pdfCustomizedRef = useRef(pdfCustomized)

    inspectionConfigRef.current = inspectionConfig
    pdfConfigRef.current = pdfConfig
    hasPdfConfigRef.current = hasPdfConfig
    pdfCustomizedRef.current = pdfCustomized

    useImperativeHandle(ref, () => ({
        getFormatPayloads: () => ({
            inspectionNameConfig: toApiPayload(inspectionConfigRef.current),
            pdfFilenameConfig: toApiPayload(pdfConfigRef.current),
            pdfCustomized: pdfCustomizedRef.current || hasPdfConfigRef.current,
        }),
    }))

    useEffect(() => {
        if (!enabled || !companyId) {
            setLoading(false)
            return undefined
        }

        let cancelled = false

        const loadConfigs = async () => {
            try {
                setLoading(true)
                setEmailSubjectLoading(true)
                const [inspectionRes, pdfRes, emailSubjectRes] =
                    await Promise.all([
                        CompanyService.getInspectionNameConfig(companyId).catch(
                            () => null,
                        ),
                        CompanyService.getPdfFilenameConfig(companyId).catch(
                            () => null,
                        ),
                        ShareEmailSubjectFormatService.get().catch(() => null),
                    ])

                if (cancelled) return

                const parsedInspection = parseNameFormatConfig(
                    inspectionRes?.InspectionNameConfig,
                )
                if (parsedInspection) {
                    setInspectionConfig(parsedInspection)
                } else {
                    setInspectionConfig(DEFAULT_INSPECTION_NAME_CONFIG)
                }

                const parsedPdf = parseNameFormatConfig(
                    pdfRes?.PdfFilenameConfig,
                )
                setHasPdfConfig(Boolean(parsedPdf))
                setPdfCustomized(Boolean(parsedPdf))
                setPdfEffectiveSource(pdfRes?.EffectiveSource || null)

                if (parsedPdf) {
                    setPdfConfig(parsedPdf)
                } else if (parsedInspection) {
                    setPdfConfig(parsedInspection)
                } else {
                    setPdfConfig(DEFAULT_PDF_FILENAME_CONFIG)
                }

                if (emailSubjectRes) {
                    setEmailSubjectPreview(emailSubjectRes.Preview || '—')
                    setEmailSubjectLegacy(
                        Boolean(emailSubjectRes.IsLegacyDefault),
                    )
                }
            } catch (error) {
                if (!cancelled) console.error(error)
            } finally {
                if (!cancelled) {
                    setLoading(false)
                    setEmailSubjectLoading(false)
                }
            }
        }

        loadConfigs()

        return () => {
            cancelled = true
        }
    }, [companyId, enabled])

    const inspectionPreview = buildFormattedName(
        withCompanyTimezone(inspectionConfig, companyTimeZone),
        sample,
    )
    // Falling back to the inspection format (no PDF format, or a saved copy equal to the inspection
    // one), the PDF name also uses the company time zone (server rule).
    const pdfOwnConfig = hasPdfConfig || pdfCustomized
    const pdfSameAsInspection =
        JSON.stringify(toApiPayload(pdfConfig)) === JSON.stringify(toApiPayload(inspectionConfig))
    const pdfPreview = buildPdfFilenamePreview(
        pdfOwnConfig && !pdfSameAsInspection
            ? pdfConfig
            : withCompanyTimezone(pdfOwnConfig ? pdfConfig : inspectionConfig, companyTimeZone),
        sample,
    )
    const getPdfSourceLabel = () => {
        if (draftOnly) {
            if (pdfCustomized || hasPdfConfig) return 'PDF Config'
            return getPdfEffectiveSourceLabel(pdfEffectiveSource)
        }
        if (hasPdfConfig) return getPdfEffectiveSourceLabel('PdfFilenameConfig')
        if (pdfEffectiveSource) return getPdfEffectiveSourceLabel(pdfEffectiveSource)
        if (inspectionConfig) {
            return getPdfEffectiveSourceLabel('InspectionNameConfig (fallback)')
        }
        return null
    }
    const pdfSourceLabel = getPdfSourceLabel()

    const applyInspectionConfig = payload => {
        const cfg = {
            titleSource: payload.titleSource,
            otherText: payload.otherText || '',
            includeDateTime: payload.includeDateTime,
            timezone: payload.timezone || 'Pacific Standard Time',
            includeUsername: payload.includeUsername,
        }
        // A PDF format that is still a copy of the inspection format follows it (the server treats
        // an equal copy as "same as the inspection name").
        const pdfIsCopy =
            JSON.stringify(toApiPayload(pdfConfig)) === JSON.stringify(toApiPayload(inspectionConfig))
        setInspectionConfig(cfg)
        if ((!hasPdfConfig && !pdfCustomized) || pdfIsCopy) {
            setPdfConfig(cfg)
        }
        setInspectionModalOpen(false)
    }

    const applyPdfConfig = payload => {
        const cfg = {
            titleSource: payload.titleSource,
            otherText: payload.otherText || '',
            includeDateTime: payload.includeDateTime,
            timezone: payload.timezone || 'Eastern Standard Time',
            includeUsername: payload.includeUsername,
        }
        setPdfConfig(cfg)
        setPdfCustomized(true)
        setHasPdfConfig(true)
        setPdfModalOpen(false)
    }

    if (!enabled || !companyId) return null

    return (
        <>
            <NameFormatLauncher
                variant="inspection"
                preview={inspectionPreview || SAMPLE_TEMPLATE_NAME}
                onConfigure={() => setInspectionModalOpen(true)}
                disabled={loading}
            />
            <NameFormatLauncher
                variant="pdf"
                preview={pdfPreview}
                sourceLabel={pdfSourceLabel}
                onConfigure={() => setPdfModalOpen(true)}
                disabled={loading}
            />
            <NameFormatLauncher
                variant="emailSubject"
                preview={emailSubjectPreview}
                sourceLabel={emailSubjectLegacy ? 'Legacy Default' : null}
                onConfigure={() => setEmailSubjectModalOpen(true)}
                disabled={loading || emailSubjectLoading}
            />

            <NameFormatModal
                open={inspectionModalOpen}
                onClose={() => setInspectionModalOpen(false)}
                onSave={applyInspectionConfig}
                loading={false}
                saveLabel={draftOnly ? 'Apply' : 'Save'}
                variant="inspection"
                initialConfig={inspectionConfig}
                sample={sample}
                companyTimeZone={companyTimeZone}
            />
            <NameFormatModal
                open={pdfModalOpen}
                onClose={() => setPdfModalOpen(false)}
                onSave={applyPdfConfig}
                loading={false}
                saveLabel={draftOnly ? 'Apply' : 'Save'}
                variant="pdf"
                initialConfig={pdfConfig}
                sample={sample}
            />
            <ShareEmailSubjectFormatModal
                open={emailSubjectModalOpen}
                onClose={() => setEmailSubjectModalOpen(false)}
                onSaved={savedPreview => {
                    setEmailSubjectPreview(savedPreview || '—')
                    setEmailSubjectLegacy(false)
                }}
            />
        </>
    )
})

CompanyNameFormats.displayName = 'CompanyNameFormats'
